import { useGetTrackAnalyticsApiV1Suspense } from "@/api/__generated__/api.schema";
import { useWorkspaceId } from "@/hooks/auth/use-workspace";
import { TrackDetail } from "@/routes/custom-music/dsp-analytics/components/details/track-detail";
import { RevenueBreakdownTable } from "@/routes/custom-music/dsp-analytics/components/graphics/revenue-breakdown-table";
import { RevenueChart } from "@/routes/custom-music/dsp-analytics/components/graphics/revenue-chart";
import { TrackFlag } from "@/routes/custom-music/dsp-analytics/components/graphics/track-flag";
import { useAnalyticsMonth } from "@/routes/custom-music/dsp-analytics/context/dsp-analytics-context";
import { getPreviousMonthRange } from "@/utils/date";
import { formatMonthString } from "@/utils/time";
import { Button, Icon, Sheet, SheetContent, Typography } from "@backpac/monica";
import { useState } from "react";

export const TrackAnalytics = () => {
  const { selectedMonthRange } = useAnalyticsMonth();
  const workspaceId = useWorkspaceId();
  const {
    data: {
      data: { items: trackAnalytics },
    },
  } = useGetTrackAnalyticsApiV1Suspense(workspaceId, {
    from: formatMonthString(selectedMonthRange[0]),
    to: formatMonthString(selectedMonthRange[1]),
  });
  // previous revenue with the same length as the current revenue
  const {
    data: {
      data: { items: previousTrackAnalytics },
    },
  } = useGetTrackAnalyticsApiV1Suspense(workspaceId, {
    from: formatMonthString(getPreviousMonthRange(selectedMonthRange)[0]),
    to: formatMonthString(getPreviousMonthRange(selectedMonthRange)[1]),
  });

  const [selectedIds, setSelectedIds] = useState<string[]>(() => trackAnalytics.slice(0, 5).map((item) => item.name));
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  return (
    <div className="flex flex-col gap-6">
      <TrackAnalytics.TrackDetailSheet selectedCode={selectedCode} setSelectedCode={setSelectedCode} />
      <RevenueChart analytics={trackAnalytics} previousAnalytics={previousTrackAnalytics} selectedIds={selectedIds} />
      <RevenueBreakdownTable
        title="Track"
        analytics={trackAnalytics}
        selectedIds={selectedIds}
        setSelectedIds={setSelectedIds}
        additionalColumns={[
          {
            label: "Track",
            render: (item) => <TrackFlag coverImageUrl={item.coverImageUrl} name={item.name} />,
            position: "start",
          },
          {
            label: "Artist",
            render: (item) => (
              <Typography size="xs" color="textPrimary">
                {item.artist}
              </Typography>
            ),
            position: "start",
          },
          {
            label: "",
            render: (item) => (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setSelectedCode(item.code);
                }}
                rightAddon={<Icon name="arrow-right" iconSize="12px" iconColor="textPrimary" />}
              >
                <Typography size="xs" color="textPrimary" weight={600}>
                  View Details
                </Typography>
              </Button>
            ),
            position: "end",
          },
        ]}
      />
    </div>
  );
};

interface TrackDetailSheetProps {
  selectedCode: string | null;
  setSelectedCode: (code: string | null) => void;
}

TrackAnalytics.TrackDetailSheet = ({ selectedCode, setSelectedCode }: TrackDetailSheetProps) => {
  return (
    <Sheet open={!!selectedCode} onOpenChange={() => setSelectedCode(null)}>
      <SheetContent className="pt-16 px-0 sm:max-w-[800px] bg-backgroundLight overflow-y-auto">
        {selectedCode != null ? <TrackDetail code={selectedCode} /> : <></>}
      </SheetContent>
    </Sheet>
  );
};
